"use client";
import Section from "../components/Section";
import Card from "../components/Card";
import Button from "../components/Button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faHandshakeAngle,
  faRocket,
  faScrewdriverWrench,
  faChartLine,
  faCheck,
} from "@fortawesome/free-solid-svg-icons";

const Partners = ({ onNavigate = () => {} }) => {
  const benefits = [
    {
      title: "Strategic Collaboration",
      description:
        "Deep integration with our engineering teams to co-develop innovative solutions that push the boundaries of telecommunications technology.",
      icon: faHandshakeAngle,
      metric: "Joint Innovation",
    },
    {
      title: "Accelerated Market Entry",
      description:
        "Leverage our global infrastructure and established relationships to rapidly expand your market reach and customer base.",
      icon: faRocket,
      metric: "Global Reach",
    },
    {
      title: "Enterprise-Grade Support",
      description:
        "Dedicated partner success teams, 24/7 technical support, and quarterly business reviews to ensure sustained growth and optimization.",
      icon: faScrewdriverWrench,
      metric: "24/7 Support",
    },
    {
      title: "Advanced Analytics Platform",
      description:
        "Real-time performance dashboards, predictive analytics, and custom reporting tools to optimize operations and drive data-driven decisions.",
      icon: faChartLine,
      metric: "Real-time Data",
    },
  ];

  const partnerTypes = [
    {
      title: "Global Telecommunications Carriers",
      description:
        "Expand your international footprint and enhance service quality through our next-generation network infrastructure and strategic peering relationships.",
      features: [
        "Direct fiber connection",
        "AI-powered quality assurance",
        "Competitive wholesale rates",
        "Dedicated technical integration",
      ],
      tier: "Tier 1",
    },
    {
      title: "Enterprise Technology Partners",
      description:
        "Integrate cutting-edge telecommunications capabilities into your enterprise solutions through our comprehensive API ecosystem and white-label offerings.",
      features: [
        "Complete API suite access",
        "White-label solutions",
        "Co-marketing programs",
        "Joint solution development",
      ],
      tier: "Strategic",
    },
    {
      title: "Innovation Ecosystem Partners",
      description:
        "Collaborate on next-generation telecommunications technologies including 5G, edge computing, and quantum-safe communications.",
      features: [
        "R&D collaboration",
        "Early access programs",
        "Technology licensing",
        "Joint patent development",
      ],
      tier: "Innovation",
    },
  ];

  const processSteps = [
    {
      step: "1",
      title: "Strategic Assessment",
      description:
        "Comprehensive evaluation of partnership potential and market opportunities",
      duration: "2-3 weeks",
    },
    {
      step: "2",
      title: "Technical Integration",
      description:
        "Deep-dive technical evaluation and integration planning with our engineering teams",
      duration: "4-6 weeks",
    },
    {
      step: "3",
      title: "Partnership Agreement",
      description:
        "Finalize commercial terms, SLAs, and technical requirements with legal review",
      duration: "2-4 weeks",
    },
    {
      step: "4",
      title: "Go-to-Market Launch",
      description:
        "Joint launch with dedicated support, monitoring, and optimization programs",
      duration: "Ongoing",
    },
  ];

  return (
    <div className="animate-fade-in">
      {/* HERO */}
      <Section className="pt-28 lg:pt-32 pb-20 lg:pb-24 overflow-visible bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
        <div className="text-center max-w-6xl mx-auto space-y-12">
          <h1 className="text-6xl md:text-8xl font-bold text-gray-900 leading-[1.12] tracking-tight">
            Partner
            <span className="block text-transparent bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text mt-6 pb-[0.22em] -mb-[0.22em] [-webkit-text-fill-color:transparent]">
              Program
            </span>
          </h1>
          <p className="text-2xl md:text-3xl text-gray-600 leading-relaxed max-w-5xl mx-auto">
            Join our exclusive network of strategic partners and unlock
            unprecedented opportunities for growth, innovation, and market
            expansion in the global telecommunications ecosystem
          </p>
        </div>
      </Section>

      {/* ADVANTAGES */}
      <Section background="gray" className="py-32">
        <div className="text-center mb-24 space-y-12">
          <h2 className="text-5xl md:text-7xl font-bold text-gray-900 leading-[1.12] tracking-tight">
            Partnership
            <span className="block text-transparent bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text mt-4 pb-[0.18em] -mb-[0.18em] [-webkit-text-fill-color:transparent]">
              Advantages
            </span>
          </h2>
          <p className="text-2xl md:text-3xl text-gray-600 max-w-5xl mx-auto leading-relaxed">
            We believe in building transformative relationships that drive
            mutual success, innovation, and create exceptional value for all
            stakeholders in the telecommunications ecosystem
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 max-w-7xl mx-auto">
          {benefits.map((benefit) => (
            <Card
              key={benefit.title}
              hover
              className="p-10 space-y-6 bg-white border-2 border-slate-200 hover:border-blue-400 transition-all duration-500 hover:shadow-2xl">
              <div className="flex items-center justify-between gap-4">
                <div className="bg-blue-600 text-white rounded-2xl p-3 sm:p-4">
                  <FontAwesomeIcon
                    icon={benefit.icon}
                    className="text-white text-xl sm:text-2xl"
                  />
                </div>
                <div className="bg-blue-100 text-blue-700 px-3 sm:px-4 py-1.5 sm:py-2 rounded-xl font-bold text-sm sm:text-base whitespace-nowrap tabular-nums leading-none flex-shrink-0">
                  {benefit.metric}
                </div>
              </div>
              <div className="space-y-4">
                <h3 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight break-words">
                  {benefit.title}
                </h3>
                <p className="text-lg md:text-xl text-gray-600 leading-relaxed">
                  {benefit.description}
                </p>
              </div>
            </Card>
          ))}
        </div>
      </Section>

      {/* OPPORTUNITIES — dark gradient like the rest */}
      <Section className="py-28 lg:py-32 relative overflow-visible bg-gradient-to-br from-blue-700 via-indigo-700 to-indigo-800 text-white">
        <div className="text-center mb-24 space-y-12">
          <h2 className="text-5xl md:text-7xl font-bold text-white leading-[1.12] tracking-tight">
            Partnership
            <span className="block text-blue-200 mt-6 pb-[0.18em] -mb-[0.18em]">
              Opportunities
            </span>
          </h2>
          <p className="text-2xl md:text-3xl text-blue-100 max-w-5xl mx-auto leading-relaxed">
            We collaborate with industry leaders across multiple verticals to
            create comprehensive telecommunications solutions that define the
            future of global connectivity
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 max-w-7xl mx-auto">
          {partnerTypes.map((type) => (
            <Card
              key={type.title}
              className="h-full p-10 space-y-8 bg-gradient-to-br from-white to-slate-50 border-2 border-slate-200 hover:border-blue-300 transition-all duration-300">
              {/* Title + Tier pill */}
              <div className="flex items-center justify-between gap-3 min-w-0">
                <h3 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight pr-1 flex-1 break-words min-w-0">
                  {type.title}
                </h3>
                <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-3 py-1 rounded-lg font-bold text-xs sm:text-sm whitespace-nowrap leading-none flex-shrink-0">
                  {type.tier}
                </div>
              </div>

              <p className="text-lg md:text-xl text-gray-600 leading-relaxed">
                {type.description}
              </p>

              <div className="space-y-3">
                {type.features.map((feature, idx) => (
                  <div key={idx} className="flex items-center text-gray-600">
                    <FontAwesomeIcon
                      icon={faCheck}
                      className="w-5 h-5 text-blue-600 mr-3 flex-shrink-0"
                    />
                    <span className="text-base md:text-lg break-words">
                      {feature}
                    </span>
                  </div>
                ))}
              </div>
            </Card>
          ))}
        </div>
      </Section>

      {/* JOURNEY — light theme to balance the page */}
      <Section className="py-32">
        <div className="text-center mb-24 space-y-12">
          <h2 className="text-5xl md:text-7xl font-bold text-gray-900 leading-[1.12] tracking-tight">
            Partnership
            <span className="block text-transparent bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text mt-6 pb-[0.18em] -mb-[0.18em]">
              Journey
            </span>
          </h2>
          <p className="text-2xl md:text-3xl text-gray-600 max-w-5xl mx-auto leading-relaxed">
            Our comprehensive partnership process ensures seamless onboarding,
            technical integration, and long-term strategic success
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-7xl mx-auto">
          {processSteps.map((item) => (
            <div key={item.step} className="text-center space-y-6">
              <div className="rounded-2xl w-16 h-16 flex items-center justify-center mx-auto shadow-sm bg-blue-50 border border-blue-200">
                <span className="text-2xl font-bold text-blue-700">
                  {item.step}
                </span>
              </div>
              <div className="space-y-4">
                <h3 className="text-xl md:text-2xl font-bold text-gray-900 break-words">
                  {item.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {item.description}
                </p>
                <div className="rounded-lg px-3 py-1 inline-block bg-blue-50 ring-1 ring-blue-200">
                  <span className="text-sm font-semibold text-blue-700 whitespace-nowrap">
                    {item.duration}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* FINAL CTA */}
      <Section className="py-28 lg:py-32 relative overflow-visible bg-gradient-to-br from-blue-700 via-indigo-700 to-indigo-800 text-white">
        <div className="text-center space-y-12 max-w-5xl mx-auto relative z-10">
          <h2 className="text-5xl md:text-7xl font-bold leading-[1.12] tracking-tight">
            Ready to Transform
            <span className="block text-blue-200 mt-6 pb-[0.18em] -mb-[0.18em]">
              Together?
            </span>
          </h2>
          <p className="text-2xl md:text-3xl text-blue-100 leading-relaxed">
            Let's collaborate to create groundbreaking telecommunications
            solutions and drive unprecedented growth in the global market.
          </p>
          <div className="pt-8">
            <Button
              size="lg"
              onClick={() => onNavigate("contact")}
              className="text-xl md:text-2xl px-12 py-6 rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-600 text-white shadow-2xl shadow-blue-900/30 ring-1 ring-white/10 transition-all duration-300">
              Initiate Partnership Discussion
            </Button>
          </div>
        </div>
      </Section>
    </div>
  );
};

export default Partners;
